import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { translations } from '../i18n';

export const LangContext = createContext();

// Helper for nested keys: t('nav.home') → translations.es.nav.home
const getNestedValue = (obj, path) =>
    path.split('.').reduce((acc, key) => acc?.[key], obj) ?? path;

// Detect language from URL path or document.lang (set by worker)
const detectLang = () => {
    const path = window.location.pathname;
    if (path.startsWith('/en')) return 'en';
    if (path.startsWith('/es')) return 'es';
    return document.documentElement.lang || localStorage.getItem('lang') || 'es';
};

export function LangProvider({ children }) {
    const [lang, setLangState] = useState(detectLang);

    const t = useCallback(
        (key) => getNestedValue(translations[lang], key),
        [lang]
    );

    const setLang = useCallback((newLang) => {
        const path = window.location.pathname;
        const hasSubpath = path.startsWith('/en') || path.startsWith('/es');

        if (hasSubpath) {
            // Worker mode: navigate to new language subpath
            const cleanPath = path.replace(/^\/(en|es)/, '') || '/';
            window.location.href = `/${newLang}${cleanPath}`;
        } else {
            // Client mode: update state and localStorage
            setLangState(newLang);
            localStorage.setItem('lang', newLang);
            document.documentElement.lang = newLang;
        }
    }, []);

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

    return (
        <LangContext.Provider value={value}>
            {children}
        </LangContext.Provider>
    );
}
